import { useId, useState } from 'react'
import { Label, Textarea } from '@teach/ui'
import {
  IMAGE_PROMPT_MAX,
  SHOW_IMAGE_PROMPTS,
  clampPrompt,
} from '@/features/editor/lib/imagePrompt'
import { ImagePromptPreview } from './ImagePromptPreview'
import { SecondLangNote } from './SecondLangNote'
import { AiPromptIcon } from './AiPromptIcon'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  /** Текст промпта; `''` = промпта нет. */
  value: string
  /** Живая правка. У блоков это запись в черновик, у курса/урока/маскота — в
   *  локальный стейт секции. */
  onChange: (next: string) => void
  /** Момент сохранения: выход из правки (только если текст реально изменился)
   *  и очистка. Значение приходит АРГУМЕНТОМ — владелец не обязан иметь его в
   *  своём стейте к этому моменту (`setState` асинхронен, а у формы курса
   *  стейт вообще живёт выше). Владельцы-черновики (блоки) проп не передают —
   *  их сохраняет кнопка панели. */
  onCommit?: (value: string) => void
  /** У места уже есть картинка — промпт показываем свёрнутым (§5.1). */
  hasImage: boolean
  disabled?: boolean
  /** У места есть слот второго языка (`imagePromptEn`). `false` — у баннера
   *  курса и маскота: их промпт один на курс, язык контента к нему не
   *  относится, и предупреждать не о чем. */
  hasSecondSlot?: boolean
}

/** Снимок правки: `draft` — редактируемый буфер, `snapshot` — значение на
 *  момент входа в правку. Внешний `value` во время правки не читается —
 *  перерисовка владельца (рефетч после PUT) не должна затирать несохранённый
 *  текст; сравнение с `snapshot` на выходе даёт признак «изменилось». */
interface EditingState {
  draft: string
  snapshot: string
}

/**
 * Общий контрол промпта картинки. Ставится РЯДОМ с `ImageUpload`, а не вместо
 * него: ручная загрузка картинки остаётся как была.
 *
 * Сохранение на выходе из поля (blur), без кнопки «Готово»: кнопка получала бы
 * клик уже после blur, и `onCommit` уходил бы дважды — у урока это два PUT
 * подряд, у курса лишний запрос. Если текст не менялся, `onCommit` не зовётся
 * вовсе — иначе «Править» без правки тоже слал бы запрос.
 */
const PromptField = ({
  value,
  onChange,
  onCommit,
  hasImage,
  disabled = false,
  hasSecondSlot = true,
}: Props) => {
  const m = useMessages(messages)
  const [editing, setEditing] = useState<EditingState | null>(null)
  const inputId = useId()
  const note = hasSecondSlot ? <SecondLangNote /> : null

  if (!editing) {
    return (
      <>
        <ImagePromptPreview
          value={value}
          hasImage={hasImage}
          disabled={disabled}
          onEdit={() => setEditing({ draft: value, snapshot: value })}
          onClear={() => {
            onChange('')
            onCommit?.('')
          }}
        />
        {note}
      </>
    )
  }

  const commitAndClose = () => {
    const { draft, snapshot } = editing
    setEditing(null)
    if (draft !== snapshot) {
      onCommit?.(draft)
    }
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId} className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <AiPromptIcon />
        {m.aiPrompt}
      </Label>
      <Textarea
        id={inputId}
        autoFocus
        value={editing.draft}
        maxLength={IMAGE_PROMPT_MAX}
        disabled={disabled}
        rows={3}
        placeholder={m.promptPlaceholder}
        className="resize-none rounded-xl"
        onChange={(e) => {
          const next = clampPrompt(e.target.value)
          setEditing((prev) => (prev ? { ...prev, draft: next } : prev))
          onChange(next)
        }}
        onBlur={commitAndClose}
      />
      <p className="text-right text-xs text-muted-foreground">
        {editing.draft.length}/{IMAGE_PROMPT_MAX}
      </p>
      {note}
    </div>
  )
}

/** Единая точка скрытия: все места промптов ставят именно этот контрол. */
export const ImagePromptField = (props: Props) =>
  SHOW_IMAGE_PROMPTS ? <PromptField {...props} /> : null
